import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import HeroSlider from "../../components/event_media/HeroSlider";
import ConferenceFilters from "../../components/event_media/ConferenceFilters";
import ConferenceCard from "../../components/event_media/ConferenceCard";
import Images from "../../utils/Images";
import eventsService from "../../api/EventsService";
import AuthenticatedFooter from "../../components/landing/AuthenticatedFooter";

export default function EventsListing() {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const heroImages = [Images.hero_img_web_three, Images.hero_img_web_four].filter(Boolean);
  const heroSlides = heroImages.length ? heroImages : [Images.hero_img_web_one];

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const response = await eventsService.getEvents();
        const list = response.list ?? (response.data?.data ?? response.data);
        const arr = Array.isArray(list) ? list : [];
        if (response.status && arr.length >= 0) {
          setEvents(arr);
        } else {
          toast.error(response.message || "Failed to load events");
          setEvents([]);
        }
      } catch (error) {
        toast.error(error?.message || "An error occurred while fetching events");
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const availableFilters = useMemo(() => {
    const types = new Set();
    events.forEach((item) => {
      (item.event_type || []).forEach((type) => {
        if (type) types.add(type.toLowerCase());
      });
    });
    return ["all", ...Array.from(types)];
  }, [events]);

  const filteredEvents = useMemo(() => {
    let filtered = [...events];
    if (selectedFilter !== "all") {
      filtered = filtered.filter((item) => {
        const eventTypes = item.event_type || [];
        return eventTypes.some(
          (type) => type?.toLowerCase() === selectedFilter
        );
      });
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const title = (item.event_title || "").toLowerCase();
        const description = (item.about_event || "").toLowerCase();
        const venue = (item.venue_name || "").toLowerCase();
        const city = (item.city || "").toLowerCase();
        const country = (item.country || "").toLowerCase();
        return (
          title.includes(query) ||
          description.includes(query) ||
          venue.includes(query) ||
          city.includes(query) ||
          country.includes(query)
        );
      });
    }
    return filtered;
  }, [events, selectedFilter, searchQuery]);

  const handleFilterChange = (value) => {
    setSelectedFilter(availableFilters.includes(value) ? value : "all");
  };

  const handleCardClick = (item) => {
    if (item.slug) {
      navigate(`/event-media-sales/event-details/${item.slug}`);
    }
  };

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <HeroSlider
        images={heroSlides}
        showBackButton
        backLink="/event-media-sales"
        backLabel="Back to Event & Media Sales"
      />

      <section className="px-4 py-12 bg-gradient-to-b from-gray-50 to-white sm:px-6 lg:px-8 flex-1">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-8">
            <div className="min-w-0">
              <div className="flex flex-wrap gap-2 items-center mb-1">
                <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                  Events
                </h2>
                <span className="inline-flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full border border-emerald-200">
                  <Sparkles size={12} />
                  For agents
                </span>
              </div>
              <p className="text-sm text-gray-600 max-w-xl">
                Events coverage and seminars you can promote to your clients.
              </p>
              <div className="mt-2 w-24 h-1 bg-emerald-500 rounded" />
            </div>
            <motion.button
              onClick={() => navigate("/event-media-sales")}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg shadow-sm transition-colors duration-200 hover:bg-emerald-100 hover:border-emerald-300 shrink-0"
            >
              <ArrowLeft size={16} />
              Back to Event & Media Sales
            </motion.button>
          </div>

          <div className="mb-8">
            <ConferenceFilters
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              customFilters={availableFilters}
              placeholder="Search events..."
            />
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="flex flex-col gap-4 items-center">
                <div className="w-10 h-10 rounded-full border-2 border-emerald-200 border-t-emerald-500 animate-spin" />
                <p className="text-sm text-gray-600">Loading events...</p>
              </div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center items-center py-16"
            >
              <div className="flex flex-col gap-4 items-center text-center max-w-sm">
                <div className="flex justify-center items-center w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">
                  <svg
                    className="w-10 h-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    No events found
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {searchQuery
                      ? "Try adjusting your search or filter criteria."
                      : "No events available at the moment."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/event-media-sales")}
                  className="mt-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  Back to Event & Media Sales
                </button>
              </div>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={`${selectedFilter}-${searchQuery}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 sm:grid-cols-2 lg:grid-cols-4"
              >
                {filteredEvents.map((item, index) => {
                  const organizerName =
                    (typeof item.organizer_name === "string" &&
                      item.organizer_name?.trim()) ||
                    (item.organizer_name?.name) ||
                    item.organizer?.name ||
                    (typeof item.organizer === "string" && item.organizer?.trim()) ||
                    item.organizer_slug ||
                    null;
                  const displayItem = { ...item, organizer_name: organizerName };

                  return (
                    <motion.div
                      key={item.id ?? item._id ?? index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.05, 0.3) }}
                      className="w-full"
                    >
                      <ConferenceCard
                        item={displayItem}
                        onClick={() => handleCardClick(displayItem)}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          )}

          {!loading && filteredEvents.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 text-sm text-gray-600"
            >
              Showing {filteredEvents.length} of {events.length} results
            </motion.div>
          )}
        </div>
      </section>

      <AuthenticatedFooter />
    </div>
  );
}
